import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { mailIntakeDelete, mailIntakeKey, mailIntakeMessagesKey, mailIntakeMessagesQuery, mailIntakeOutboxKey, mailIntakeOutboxQuery, mailIntakeQuery, mailIntakeResend, mailIntakeRetry, mailIntakeSave, mailIntakeTest, type MailIntakeMessageFilters, type MailIntakePage, type MailIntakeSetting, type MailIntakeTest } from '../../api/settingsMailIntake'
import { Spinner } from '../../ui/Spinner'
import { useMsg } from './AutonomyPage'
import './mailIntake.css'

/**
 * Sectiunea "Email intake" de sub biroul de service: casuta pe care o
 * citeste platforma pentru cererile catre Adam (docs/plan-adam-prin-email.md,
 * F1). Acelasi scop ca panoul biroului (`dscope`): firma, sau casuta generala
 * a furnizorului pentru un Global Admin. Conectarea, modul (shadow / live),
 * politica de intrare, proba, si ce a venit pe email cu ce s-a hotarat.
 */
const DECISION: Record<string, [string, string]> = {
  ticket_new: ['new ticket', '#2e7d32'],
  ticket_reply: ['reply in ticket', '#1f5f8b'],
  rejected: ['refused', '#b54708'],
  ignored: ['ignored', '#6b7480'],
  error: ['error', '#b42318'],
  pending: ['pending', '#6b7480'],
}

const UNKNOWN: Record<string, string> = {
  reject: 'reply that the address is not known and do nothing else',
  ticket_human: 'open a ticket for a colleague, without the agent',
  ignore: 'ignore the message (moved to the failed folder)',
  verify: 'verify: a one-time code to an Active Directory account of the company',
}

function blank(m365: string | null): Form {
  return {
    enabled: false, mode: 'shadow', kind: m365 ? 'graph' : 'imap', address: '', graph_tenant_id: m365 ?? '', imap_host: '', imap_port: '993', imap_tls: true, imap_user: '', password: '',
    folder_in: 'INBOX', folder_done: 'OD-Done', folder_failed: 'OD-Failed', unknown_senders: 'ticket_human', require_dmarc: true, reply_mode: 'mailbox', max_per_sender_day: '20',
    authserv_id: '',
  }
}

interface Form {
  enabled: boolean; mode: 'shadow' | 'live'; kind: 'graph' | 'imap'; address: string; graph_tenant_id: string; imap_host: string; imap_port: string; imap_tls: boolean; imap_user: string; password: string
  folder_in: string; folder_done: string; folder_failed: string; unknown_senders: string; require_dmarc: boolean; reply_mode: string; max_per_sender_day: string
  authserv_id: string
}

function fromSetting(x: MailIntakeSetting | null, m365: string | null): Form {
  if (!x) return blank(m365)
  return {
    enabled: x.enabled, mode: x.mode === 'live' ? 'live' : 'shadow', kind: x.kind, address: x.address, graph_tenant_id: x.graph_tenant_id ?? m365 ?? '', imap_host: x.imap_host ?? '', imap_port: String(x.imap_port), imap_tls: x.imap_tls, imap_user: x.imap_user ?? '', password: '',
    folder_in: x.folder_in, folder_done: x.folder_done, folder_failed: x.folder_failed, unknown_senders: x.unknown_senders, require_dmarc: x.require_dmarc, reply_mode: x.reply_mode, max_per_sender_day: String(x.max_per_sender_day),
    authserv_id: x.authserv_id ?? '',
  }
}

function trustLine(r: { sender_kind: string; dmarc: string | null; trust_level?: number | null; authserv_ok?: boolean | null; requester_class?: string | null }): string {
  const dmarc = r.dmarc ? `DMARC ${r.dmarc}` : 'no DMARC'
  const auth = r.authserv_ok === true ? 'authserv ok' : r.authserv_ok === false ? 'other host' : 'unknown'
  const trust = r.trust_level === null || r.trust_level === undefined ? null : `trust ${r.trust_level}`
  const bits = [r.sender_kind]
  if (trust !== null) bits.push(`${trust} (${dmarc}, ${auth})`)
  else bits.push(dmarc)
  if (r.requester_class) bits.push(r.requester_class)
  return bits.join(' · ')
}

function fmt(d: string | null): string {
  if (!d) return ''
  const t = new Date(d.replace(' ', 'T') + 'Z')
  if (Number.isNaN(t.getTime())) return d
  return `${String(t.getDate()).padStart(2, '0')} ${t.toLocaleString('en-GB', { month: 'short' })} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`
}

const ADVANCED_FIELDS = ['folder_in', 'folder_done', 'folder_failed', 'authserv_id', 'reply_mode']

type Tab = 'setup' | 'in' | 'replies'

function state(x: MailIntakeSetting | null): { key: 'none' | 'off' | 'shadow' | 'live'; label: string } {
  if (!x) return { key: 'none', label: 'Not set up' }
  if (!x.enabled) return { key: 'off', label: 'Off' }
  return x.mode === 'live' ? { key: 'live', label: 'Live' } : { key: 'shadow', label: 'Watching (shadow)' }
}

export function MailIntakePanel({ scope }: { scope: 'company' | 'sp'; companyId?: number; companyName?: string }) {
  const client = useQueryClient()
  const page = useQuery(mailIntakeQuery(scope))
  const d = page.data
  const [f, setF] = useState<Form | null>(null)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)
  const [probe, setProbe] = useState<MailIntakeTest | null>(null)
  const [tab, setTab] = useState<Tab | null>(null)
  const [advanced, setAdvanced] = useState(false)
  const [msg, say] = useMsg()
  const failedReplies = useQuery({ ...mailIntakeOutboxQuery(scope), enabled: Boolean(d?.setting) }).data?.rows.filter((r) => r.state === 'failed').length ?? 0

  useEffect(() => {
    if (d) {
      setF(fromSetting(d.setting, d.m365Tenant))
      setFieldError(null)
    }
  }, [d])

  if (page.error instanceof ApiError && page.error.status === 403) return null
  if (!d || !f) return <div className="panel panel-default" id="odmi"><div className="panel-body"><Spinner /></div></div>

  const x = d.setting
  const shownTab: Tab = tab ?? (x?.enabled ? 'in' : 'setup')
  const set = (patch: Partial<Form>) => setF({ ...f, ...patch })
  const put = (next: MailIntakePage) => client.setQueryData(mailIntakeKey(scope), next)
  const err = (field: string) => (fieldError?.field === field ? fieldError.message : null)
  const cls = (field: string) => 'form-group' + (err(field) ? ' has-error' : '')
  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.details && typeof e.details === 'object' && 'field' in e.details) {
      const det = e.details as { field?: string; message?: string }
      const field = String(det.field ?? '')
      setFieldError({ field, message: det.message ?? e.message })
      if (ADVANCED_FIELDS.includes(field)) setAdvanced(true)
    }
    say(e instanceof ApiError && e.message ? e.message : 'Failed', 'bad')
  }

  const save = () => {
    setFieldError(null)
    say(f.enabled && !x?.enabled ? 'Saving and testing the mailbox...' : 'Saving...', 'wait')
    void mailIntakeSave({
      scope, enabled: f.enabled ? '1' : '0', mode: f.mode, kind: f.kind, address: f.address, graph_tenant_id: f.graph_tenant_id, imap_host: f.imap_host, imap_port: f.imap_port, imap_tls: f.imap_tls ? '1' : '0',
      imap_user: f.imap_user, password: f.password, folder_in: f.folder_in, folder_done: f.folder_done, folder_failed: f.folder_failed, unknown_senders: f.unknown_senders,
      require_dmarc: f.require_dmarc ? '1' : '0', reply_mode: f.reply_mode, max_per_sender_day: f.max_per_sender_day,
      authserv_id: f.authserv_id,
    })
      .then((next) => {
        put(next)
        setProbe(null)
        const on = next.setting?.enabled
        say(on && !x?.enabled ? 'Saved. The mailbox answered and is now read.' : 'Saved', 'ok')
        void client.invalidateQueries({ queryKey: mailIntakeMessagesKey(scope) })
      })
      .catch((e) => { fail(e); void client.invalidateQueries({ queryKey: mailIntakeKey(scope) }) })
  }
  const test = () => {
    say('Testing the connection...', 'wait')
    void mailIntakeTest(scope)
      .then((r) => { setProbe(r); say(r.ok ? 'The mailbox answered' : r.error ?? 'Failed', r.ok ? 'ok' : 'bad'); void client.invalidateQueries({ queryKey: mailIntakeKey(scope) }) })
      .catch(fail)
  }
  const remove = () => {
    if (!window.confirm('Remove this mailbox? Reading stops and the saved connection is deleted. Messages already read stay in the list.')) return
    say('Removing...', 'wait')
    void mailIntakeDelete(scope).then((next) => { put(next); setProbe(null); say('Removed', 'ok'); setTab('setup') }).catch(fail)
  }

  const st = state(x)
  const runState: 'off' | 'shadow' | 'live' = f.enabled ? f.mode : 'off'
  const pick = (v: 'off' | 'shadow' | 'live') => set(v === 'off' ? { enabled: false } : { enabled: true, mode: v })
  const starting = f.enabled && !x?.enabled
  const enabledHelp = err('enabled')
    ?? (x?.enabled
      ? 'To change the connection, set it to Off and save; then pick Watching or Live and save again.'
      : 'Save runs Test first: the mailbox starts only if it answers.')

  return (
    <div className="panel panel-default" id="odmi" data-scope={scope}>
      <div className="panel-body">
        <div className="odmi-head">
          <div>
            <div className="odmi-title">
              <h3>Email intake</h3>
              <span className={`odmi-badge is-${st.key}`} id="odmi-state">{st.label}</span>
            </div>
            <p className="odmi-lead">Requests sent to this mailbox become tickets for the service desk, and the agent answers in the same email thread, within what the desk policy allows.</p>
          </div>
        </div>

        {x && (
          <div className="odmi-facts" id="odmi-facts">
            <div><span>Mailbox</span><strong>{x.address}</strong></div>
            <div>
              <span>Connection</span>
              {x.checked_at
                ? <span className={x.check_ok ? 'is-good' : 'is-bad'}>{x.check_ok ? 'tested ok' : `test failed: ${x.check_error || 'no answer'}`} · {fmt(x.checked_at)}</span>
                : <span className="is-warn">not tested yet</span>}
            </div>
            <div>
              <span>Last read</span>
              {x.last_poll_at
                ? <span className={x.last_error ? 'is-bad' : undefined}>{fmt(x.last_poll_at)}{x.last_error ? `: ${x.last_error}` : ''}{x.fail_count > 1 ? ` (${x.fail_count} times in a row)` : ''}</span>
                : <span>{x.enabled ? 'within a minute' : 'not read while off'}</span>}
            </div>
          </div>
        )}
        {!d.ready && <div className="odmi-alert">The email intake tables are missing: run the database migrations first. The form is shown, but nothing can be saved yet.</div>}
        {!d.cryptoReady && <div className="odmi-alert is-warn">Encryption is not configured on this server, so IMAP passwords cannot be stored.</div>}

        {x && (
          <div className="odmi-tabs" role="tablist" aria-label="Email intake">
            {([['setup', 'Setup'], ['in', 'What came in'], ['replies', 'Replies']] as const).map(([k, label]) => (
              <button key={k} type="button" role="tab" id={`odmi-tab-${k}`} aria-selected={shownTab === k} aria-controls={`odmi-pane-${k}`} onClick={() => setTab(k)}>
                {label}{k === 'replies' && failedReplies > 0 && <span className="odmi-count">{failedReplies} failed</span>}
              </button>
            ))}
          </div>
        )}

        <div id="odmi-pane-setup" role="tabpanel" hidden={shownTab !== 'setup'} className="odmi-setup-wrap">
        <div className="odmi-setup">
          <section className="odmi-step">
            <div className="odmi-step-no" aria-hidden="true">1</div>
            <div>
              <h4>The mailbox</h4>
              <p className="odmi-step-lead">The address people write to, and how the platform signs in to it.</p>
              <div className={cls('address')}>
                <label htmlFor="odmi-address">Mailbox address</label>
                <input id="odmi-address" className="form-control input-sm" type="email" placeholder="support@company.com" value={f.address} onChange={(e) => set({ address: e.target.value })} />
                {err('address') && <span className="help-block">{err('address')}</span>}
              </div>
              <span className="odmi-label" id="odmi-kind-label">Connect with</span>
              <div className="odmi-choices" role="radiogroup" aria-labelledby="odmi-kind-label">
                <label className={`odmi-choice${d.graphConfigured ? '' : ' is-unavailable'}`}>
                  <input type="radio" name="odmi-kind" id="odmi-kind-graph" value="graph" checked={f.kind === 'graph'} onChange={() => set({ kind: 'graph' })} />
                  <strong>Microsoft 365</strong>
                  <span>Signs in through the company&apos;s Microsoft 365 link, no password.{!d.graphConfigured && <em> Not configured on this server.</em>}</span>
                </label>
                <label className={`odmi-choice${d.imapAvailable ? '' : ' is-unavailable'}`}>
                  <input type="radio" name="odmi-kind" id="odmi-kind-imap" value="imap" checked={f.kind === 'imap'} onChange={() => set({ kind: 'imap' })} />
                  <strong>IMAP</strong>
                  <span>Any mail server, with a user and password.{!d.imapAvailable && <em> Not available on this server.</em>}</span>
                </label>
              </div>
              {f.kind === 'graph' && (
                <div className={cls('graph_tenant_id')}>
                  <label htmlFor="odmi-tenant">Tenant id</label>
                  <input id="odmi-tenant" className="form-control input-sm" value={f.graph_tenant_id} placeholder="00000000-0000-0000-0000-000000000000" onChange={(e) => set({ graph_tenant_id: e.target.value })} />
                  <span className="help-block">
                    {err('graph_tenant_id') ?? (d.m365Tenant ? 'Taken from Securitee → Microsoft 365. ' : '')}
                    The tenant consents to <code>Mail.ReadWrite</code> and <code>Mail.Send</code> in Securitee → Microsoft 365 → Connect. Its Exchange admin should limit the application to this mailbox; Test checks that.
                  </span>
                </div>
              )}
              {f.kind === 'imap' && (
                <>
                  <div className={cls(err('imap_port') ? 'imap_port' : 'imap_host')}>
                    <label htmlFor="odmi-host">Server and port</label>
                    <div className="odmi-host-row">
                      <input id="odmi-host" className="form-control input-sm" placeholder="mail.company.com" value={f.imap_host} onChange={(e) => set({ imap_host: e.target.value })} />
                      <input id="odmi-port" className="form-control input-sm" aria-label="Port" type="number" min={1} max={65535} value={f.imap_port} onChange={(e) => set({ imap_port: e.target.value })} />
                    </div>
                    {(err('imap_host') ?? err('imap_port')) && <span className="help-block">{err('imap_host') ?? err('imap_port')}</span>}
                  </div>
                  <label className="odmi-check"><input type="checkbox" id="odmi-tls" checked={f.imap_tls} onChange={(e) => set({ imap_tls: e.target.checked })} /> <span>Encrypted connection (TLS, certificate verified) <span className="odmi-help">Keep it on; port 993 is the usual one.</span></span></label>
                  <div className="odmi-grid">
                    <div className={cls('imap_user')}>
                      <label htmlFor="odmi-user">User</label>
                      <input id="odmi-user" className="form-control input-sm" value={f.imap_user} placeholder={f.address || 'usually the mailbox address'} onChange={(e) => set({ imap_user: e.target.value })} />
                      {err('imap_user') && <span className="help-block">{err('imap_user')}</span>}
                    </div>
                    <div className={cls('password')}>
                      <label htmlFor="odmi-password">Password</label>
                      <input id="odmi-password" className="form-control input-sm" type="password" autoComplete="new-password" value={f.password} placeholder={x?.password_set ? 'Saved. Leave empty to keep it.' : ''} onChange={(e) => set({ password: e.target.value })} />
                      <span className="help-block">{err('password') ?? 'Stored encrypted; never shown again.'}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="odmi-step">
            <div className="odmi-step-no" aria-hidden="true">2</div>
            <div>
              <h4>Who may write</h4>
              <p className="odmi-step-lead">Known people of the company (contacts and operators) whose message passes DMARC can ask the desk for what its policy allows.</p>
              <div className={cls('unknown_senders')}>
                <label htmlFor="odmi-unknown">Messages from unknown senders</label>
                <select id="odmi-unknown" className="form-control input-sm" value={f.unknown_senders} onChange={(e) => set({ unknown_senders: e.target.value })}>
                  {d.unknownSenders.map((u) => <option key={u} value={u}>{UNKNOWN[u] ?? u}</option>)}
                </select>
                {err('unknown_senders') && <span className="help-block">{err('unknown_senders')}</span>}
                {f.unknown_senders === 'verify' && (
                  <span id="odmi-verify-help" className="help-block">
                    A sender whose address is an AD account of the company gets a one-time code by email; verified senders are served read-only until an operator makes them a contact. Without a DMARC pass, or when the account is disabled or privileged, a colleague gets the ticket.
                  </span>
                )}
              </div>
              <label className="odmi-check">
                <input type="checkbox" id="odmi-dmarc" checked={f.require_dmarc} onChange={(e) => set({ require_dmarc: e.target.checked })} />
                <span>Without a DMARC pass, the agent only reads and answers; nothing runs on a machine <span className="odmi-help">Recommended. DMARC proves the message really comes from the sender&apos;s domain.</span></span>
              </label>
              <div className={cls('max_per_sender_day')} style={{ marginBottom: 0 }}>
                <label htmlFor="odmi-max">Limit per sender</label>
                <div className="odmi-inline">
                  <input type="number" min={0} max={1000} id="odmi-max" className="form-control input-sm" value={f.max_per_sender_day} onChange={(e) => set({ max_per_sender_day: e.target.value })} />
                  <span>messages a day <span className="odmi-help" style={{ display: 'inline' }}>(0 = no limit)</span></span>
                </div>
                {err('max_per_sender_day') && <span className="help-block">{err('max_per_sender_day')}</span>}
              </div>
            </div>
          </section>

          <section className="odmi-step">
            <div className="odmi-step-no" aria-hidden="true">3</div>
            <div className={err('enabled') || err('mode') ? 'has-error' : undefined}>
              <h4>Start reading</h4>
              <p className="odmi-step-lead">Start with Watching for a few days, look at What came in, then switch to Live. Reading starts from now: older mail in the folder is left alone.</p>
              <div className="odmi-choices" role="radiogroup" aria-label="Reading" aria-describedby="odmi-enabled-help">
                <label className="odmi-choice">
                  <input type="radio" name="odmi-state" id="odmi-state-off" value="off" checked={runState === 'off'} onChange={() => pick('off')} />
                  <strong>Off</strong>
                  <span>Nothing is read.</span>
                </label>
                <label className="odmi-choice">
                  <input type="radio" name="odmi-state" id="odmi-state-shadow" value="shadow" checked={runState === 'shadow'} onChange={() => pick('shadow')} />
                  <strong>Watching (shadow)</strong>
                  <span>Writes down what it would do. No ticket, no reply, nothing moved.</span>
                </label>
                <label className="odmi-choice">
                  <input type="radio" name="odmi-state" id="odmi-state-live" value="live" checked={runState === 'live'} onChange={() => pick('live')} />
                  <strong>Live</strong>
                  <span>Opens tickets, answers by email, moves handled mail.</span>
                </label>
              </div>
              <span className="odmi-help" id="odmi-enabled-help">{enabledHelp}</span>
            </div>
          </section>

          <details className="odmi-advanced" id="odmi-advanced" open={advanced} onToggle={(e) => setAdvanced((e.currentTarget as HTMLDetailsElement).open)}>
            <summary>Advanced: folders, authentication host, reply address</summary>
            <span className="odmi-label">Folders</span>
            <div className="odmi-grid">
              {(['folder_in', 'folder_done', 'folder_failed'] as const).map((k) => (
                <div key={k} className={cls(k)}>
                  <label htmlFor={`odmi-${k}`} style={{ fontWeight: 400 }}>{k === 'folder_in' ? 'Read from' : k === 'folder_done' ? 'Move handled mail to' : 'Move failed mail to'}</label>
                  <input id={`odmi-${k}`} className="form-control input-sm" value={f[k]} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />
                  {err(k) && <span className="help-block">{err(k)}</span>}
                </div>
              ))}
            </div>
            <p className="odmi-help" style={{ marginTop: -6, marginBottom: 12 }}>Handled messages are moved, never deleted.</p>
            <div className={cls('authserv_id')}>
              <label htmlFor="odmi-authserv">Authentication-Results host (authserv-id)</label>
              <input id="odmi-authserv" className="form-control input-sm" style={{ maxWidth: '24em' }} value={f.authserv_id} placeholder="learned by Test from the newest message" onChange={(e) => set({ authserv_id: e.target.value })} />
              <span className="help-block">
                {err('authserv_id') ?? (f.authserv_id.trim() === ''
                  ? 'Not set: the agent only reads. Test learns it from the mailbox host.'
                  : 'The agent trusts dmarc=pass only from this host.')}
              </span>
            </div>
            <div className={cls('reply_mode')}>
              <label htmlFor="odmi-reply">Replies are sent</label>
              <select id="odmi-reply" className="form-control input-sm" style={{ maxWidth: '24em' }} value={f.reply_mode} onChange={(e) => set({ reply_mode: e.target.value })}>
                <option value="mailbox">from this mailbox, in the same thread</option>
                <option value="noreply">from the provider&apos;s no-reply address</option>
              </select>
            </div>
          </details>

          <div className="odmi-actions">
            <button type="button" className="btn btn-primary btn-sm" id="odmi-save" onClick={save} disabled={!d.ready}>{starting ? 'Save, test and start' : 'Save'}</button>
            {x && <button type="button" className="btn btn-default btn-sm" id="odmi-test" onClick={test}>Test connection</button>}
            <span aria-live="polite">{msg}</span>
            {x && <button type="button" className="btn btn-link btn-sm odmi-remove" id="odmi-delete" onClick={remove}>Remove mailbox</button>}
          </div>
          {probe && (
            <div id="odmi-probe" className={`odmi-probe${probe.ok && probe.scope_ok !== false && probe.authserv_match !== false ? '' : ' is-bad'}`} role="status">
              {probe.ok ? (
                <>
                  <div><strong>Connected.</strong> Folders: {probe.folders.join(', ') || 'none listed'}.</div>
                  {probe.missing_folders.length > 0 && <div className="is-warn">Missing folders: {probe.missing_folders.join(', ')}. Create them in the mailbox before switching on.</div>}
                  {probe.scope_ok === true && <div>The application is limited to this mailbox: another mailbox of the tenant was refused.</div>}
                  {probe.scope_ok === false && <div className="is-bad">The application can read OTHER mailboxes of this tenant. Ask the Exchange admin for an application access policy limited to this mailbox before switching on.</div>}
                </>
              ) : (
                <div className="is-bad">{probe.error}</div>
              )}
              {probe.authserv_seen != null && probe.authserv_seen !== '' && (
                <div className={probe.authserv_match === false ? 'is-bad' : undefined}>
                  Authentication host seen: {probe.authserv_seen}{probe.authserv_match === false ? ' (does not match the saved host)' : ''}
                </div>
              )}
            </div>
          )}
        </div>
        <aside className="odmi-aside" aria-label="Getting it running">
          <h4>Getting it running</h4>
          <ol>
            {([
              [Boolean(x), 'Mailbox saved', 'Address and sign-in, step 1.'],
              [x?.check_ok === true, 'Connection tested', 'Save runs Test when you start reading.'],
              [Boolean(x?.enabled), 'Watching for a few days', 'Nothing is sent; check What came in.'],
              [Boolean(x?.enabled) && x?.mode === 'live', 'Live', 'Tickets open and answers go out.'],
            ] as const).map(([done, title, hint]) => (
              <li key={title} className={done ? 'is-done' : undefined}>
                <strong>{title}</strong>
                <span>{hint}</span>
              </li>
            ))}
          </ol>
        </aside>
        </div>

        {x && <div id="odmi-pane-in" role="tabpanel" hidden={shownTab !== 'in'} className="odmi-activity"><MailIntakeMessages scope={scope} /></div>}
        {x && <div id="odmi-pane-replies" role="tabpanel" hidden={shownTab !== 'replies'} className="odmi-activity"><MailIntakeOutbox scope={scope} /></div>}
      </div>
    </div>
  )
}

const OUTBOX_TONE: Record<string, string> = { queued: '#1f5f8b', sent: '#2e7d32', failed: '#b42318' }

/** Raspunsurile din outbox - fila Replies cu Resend pe failed. */
function MailIntakeOutbox({ scope }: { scope: 'company' | 'sp' }) {
  const client = useQueryClient()
  const q = useQuery(mailIntakeOutboxQuery(scope))
  const rows = q.data?.rows ?? []
  const [busy, setBusy] = useState(0)
  const [note, setNote] = useState<{ id: number; ok: boolean; text: string } | null>(null)

  const resend = (id: number) => {
    setBusy(id)
    setNote(null)
    void mailIntakeResend(scope, id)
      .then((r) => {
        setNote({ id, ok: r.ok, text: r.ok ? 'queued again' : r.error ?? 'Not resent' })
        void client.invalidateQueries({ queryKey: mailIntakeOutboxKey(scope) })
      })
      .catch((e: unknown) => setNote({ id, ok: false, text: e instanceof ApiError && e.message ? e.message : 'Not resent' }))
      .finally(() => setBusy(0))
  }

  return (
    <div id="odmi-outbox">
      <p className="odmi-help" style={{ margin: '0 0 8px' }}>Every answer the platform sends from this mailbox, with its state. A failed one can be sent again.</p>
      {rows.length === 0 ? (
        <div className="odmi-empty">No replies yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
        <table className="table table-condensed" style={{ fontSize: 12, marginTop: 6, minWidth: '40em' }}>
          <thead><tr><th style={{ width: 110 }}>Created</th><th style={{ width: 180 }}>To</th><th style={{ width: 70 }}>Kind</th><th style={{ width: 70 }}>Ticket</th><th style={{ width: 80 }}>State</th><th>Error</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{fmt(r.created_at)}</td>
                <td>{r.to}<div className="text-muted" style={{ fontSize: 11 }}>{r.subject}</div></td>
                <td><span>{r.kind || 'reply'}</span>{r.lang ? <div className="text-muted" style={{ fontSize: 11 }}>{r.lang}</div> : null}</td>
                <td>{r.ticket_id > 0 ? <a href={`/app/easeedesk/ticket/${r.ticket_id}`}>#{r.ticket_id}</a> : '—'}</td>
                <td>
                  <span style={{ fontWeight: 600, color: OUTBOX_TONE[r.state] ?? '#6b7480' }}>{r.state}</span>
                  <div className="text-muted" style={{ fontSize: 11 }}>attempt {r.attempts}{r.message_id_set ? ' · Message-ID' : ''}</div>
                </td>
                <td>
                  {r.error || (r.sent_at ? `sent ${fmt(r.sent_at)}` : '')}
                  {r.state === 'failed' && (
                    <div style={{ marginTop: 4 }}>
                      <button type="button" className="btn btn-default btn-xs odmi-resend" disabled={busy === r.id} onClick={() => resend(r.id)}>Resend</button>
                      {note?.id === r.id && <span style={{ marginLeft: 6, color: note.ok ? '#2e7d32' : '#b42318' }}>{note.text}</span>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  )
}

/** Ce a venit pe email si ce s-a hotarat - `it_mail_intake_messages`, cele mai noi intai. */
function MailIntakeMessages({ scope }: { scope: 'company' | 'sp' }) {
  const client = useQueryClient()
  const [filters, setFilters] = useState<MailIntakeMessageFilters>({ page: 1 })
  const [search, setSearch] = useState('')
  const q = useQuery(mailIntakeMessagesQuery(scope, filters))
  const rows = q.data?.rows ?? []
  const page = q.data?.page ?? 1
  const pages = Math.max(1, Math.ceil((q.data?.total ?? rows.length) / (q.data?.per_page ?? Math.max(1, rows.length))))
  const filtered = Boolean(filters.decision || filters.q || filters.has_run)
  const [busy, setBusy] = useState(0)
  const [note, setNote] = useState<{ id: number; ok: boolean; text: string } | null>(null)

  /* Mesajul se citeste din nou din casuta (dupa id-ul lui Microsoft 365) si trece prin toti pasii. */
  const retry = (id: number) => {
    setBusy(id)
    setNote(null)
    void mailIntakeRetry(scope, id)
      .then((r) => {
        setNote({ id, ok: r.ok, text: r.ok ? `done: ${DECISION[r.decision ?? '']?.[0] ?? r.decision ?? ''}` : r.error ?? 'Not retried' })
        void client.invalidateQueries({ queryKey: mailIntakeMessagesKey(scope) })
      })
      .catch((e: unknown) => setNote({ id, ok: false, text: e instanceof ApiError && e.message ? e.message : 'Not retried' }))
      .finally(() => setBusy(0))
  }

  return (
    <div id="odmi-messages">
      <p className="odmi-help" style={{ margin: '0 0 8px' }}>Every message read, newest first, with what was decided and why.</p>
      <form
        className="odmi-filters"
        style={{ display: 'flex', gap: 6, marginTop: 6, fontSize: 12 }}
        onSubmit={(e) => {
          e.preventDefault()
          setFilters({ ...filters, q: search.trim(), page: 1 })
        }}
      >
        <select className="form-control input-sm" aria-label="Decision" style={{ width: 160 }} value={filters.decision ?? ''}
          onChange={(e) => setFilters({ ...filters, decision: e.target.value, page: 1 })}>
          <option value="">Every decision</option>
          {Object.entries(DECISION).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <label style={{ fontWeight: 400, display: 'flex', alignItems: 'center', gap: 4 }}>
          <input type="checkbox" checked={filters.has_run === '1'} onChange={(e) => setFilters({ ...filters, has_run: e.target.checked ? '1' : '', page: 1 })} /> with a desk run
        </label>
        <input className="form-control input-sm" aria-label="Search messages" placeholder="Sender or subject" style={{ width: 200 }} value={search} onChange={(e) => setSearch(e.target.value)} />
        <button type="submit" className="btn btn-default btn-sm">Search</button>
      </form>
      {rows.length === 0 && filtered ? (
        <div className="odmi-empty">No message matches.</div>
      ) : rows.length === 0 ? (
        <div className="odmi-empty">Nothing yet. Messages appear here within a minute of arriving.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
        <table className="table table-condensed" style={{ fontSize: 12, marginTop: 6, minWidth: '48em' }}>
          <thead><tr><th style={{ width: 110 }}>Received</th><th style={{ width: 200 }}>From</th><th>Subject</th><th style={{ width: 56 }}>Lang</th><th style={{ width: 56 }}>Ack</th><th style={{ width: 120 }}>Decision</th><th>Why</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const [label, tone] = DECISION[r.decision] ?? [r.decision, '#6b7480']
              return (
                <tr key={r.id}>
                  <td>{fmt(r.received_at)}</td>
                  <td>
                    {r.from}
                    <div className="text-muted" style={{ fontSize: 11 }}>{trustLine(r)}</div>
                  </td>
                  <td>{r.subject}</td>
                  <td className="text-muted">{r.lang ?? '—'}</td>
                  <td className="text-muted">{r.acked_at ? 'yes' : '—'}</td>
                  <td>
                    <span style={{ fontWeight: 600, color: tone }}>{label}</span>
                    {r.shadow && <div className="text-muted" style={{ fontSize: 11 }}>shadow</div>}
                    {r.ticket_id > 0 && !r.shadow && <div><a href={`/app/easeedesk/ticket/${r.ticket_id}`}>#{r.ticket_id}</a></div>}
                    {(r.run_id ?? 0) > 0 && <div><a className="odmi-run" href={`/app/autonomy/desk?run=${r.run_id}`}>desk: {r.run_state}</a></div>}
                    {(r.attachments ?? 0) > 0 && <div className="text-muted odmi-files" style={{ fontSize: 11 }}>{r.attachments} attachment{r.attachments === 1 ? '' : 's'}</div>}
                  </td>
                  <td>
                    {r.reason}
                    {r.retry && (
                      <div style={{ marginTop: 4 }}>
                        <button type="button" className="btn btn-default btn-xs odmi-retry" disabled={busy === r.id} onClick={() => retry(r.id)}>Retry</button>
                        {note?.id === r.id && <span style={{ marginLeft: 6, color: note.ok ? '#2e7d32' : '#b42318' }}>{note.text}</span>}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>
      )}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
          <button type="button" className="btn btn-default btn-xs" disabled={page <= 1} onClick={() => setFilters({ ...filters, page: page - 1 })}>Previous</button>
          <span>Page {page} of {pages}</span>
          <button type="button" className="btn btn-default btn-xs" disabled={page >= pages} onClick={() => setFilters({ ...filters, page: page + 1 })}>Next</button>
        </div>
      )}
    </div>
  )
}
